import { MutationCache, QueryClient } from '@tanstack/react-query';
import { normalizeError } from '@/shared/errors/normalizeError';
import { routeErrorEnvelopeToast } from '@/shared/errors/routeErrorEnvelope';

/**
 * Global mutation error fallback. Fires for every mutation error AFTER any
 * caller-supplied onError runs. The shared toast dedup window in
 * routeErrorEnvelopeToast / markEnvelopeHandled ensures this stays silent
 * when useApiError already showed the right surface — so callers that handle
 * errors keep their UX, and callers that forgot to attach onError no longer
 * fail silently.
 *
 * This is the safety net that closes the "user has to open the network tab to
 * find out what failed" gap.
 */
const mutationCache = new MutationCache({
  onError: (error) => {
    const envelope = normalizeError(error);
    routeErrorEnvelopeToast(envelope);
  },
});

export const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 mins before data is "stale"
      gcTime: 1000 * 60 * 10,   // 10 mins before unused cache removed
      retry: 1,
      refetchOnWindowFocus: true,
    },
    mutations: {
      // Mutations are POST/PUT/DELETE — not idempotent. Auto-retrying a
      // mutation whose handler threw client-side (e.g. response-shape mismatch,
      // normalize error) silently doubles the request and trips server-side
      // duplicate guards. Callers retry deliberately if they need to.
      retry: 0,
    },
  },
});
